import { FC, useState } from "react";

import { NamedRow } from "../../../../../../components/GeneralUI/NamedRow/NamedRow";
import { AddComponentsButton } from "../../components/AddComponentsButton";
import { SlideOverWrapper } from "../../SlideOverWrapper/SlideOverWrapper";
import {
  PureVoidFunc,
  VoidBooleanPromise,
} from "../../../../../../helpers/functions/types";
import { FieldsGroup } from "./FieldsGroup";
import { AppearedContent } from "../../../../../../components/DataDisplay/AppearedContent";
import { SearchBox } from "../../../../../../components/Interaction/SearchBox/SearchBox";
import { useSlideOverSearch } from "../../hooks/useSlideOverSearch";
import { InputFieldProps, FieldsType } from "./types";
import { useRowChecks } from "./helpers/useRowChecks";
import { showItem } from "./helpers/showItem";
import { FieldsGroupBadges, PatternBadge } from "./FieldsGroupBadges";
import { patternMessage } from "./helpers/duplicateDataMessage";
import { NamedField } from "../NamedField";
import { FIELDS_PROPS } from "./constants";

/**
 * A NamedRow (title + saved entries count) that opens a slide-over for editing a list of entries.
 *
 * Data flow: the consumer owns the draft entries in local state, usually builds the handlers and
 * `fieldRender` with `getFieldsProps` (helpers/getFieldsProps.ts), and passes them in here.
 * The wrapper renders one FieldsGroup per row, the search box, the add button,
 * the per-row duplicate / built-in warnings, and the submit button.
 * Rows: a fully blank row is allowed (the consumer drops it on save); a half-filled row
 * (some importantFields empty) gets an error badge and blocks submit. Inputs carry no native `required`.
 * With several importantFields, a complete or blank row shows that rule as a neutral hint while focus is inside it (CSS group-focus-within).
 * Inputs with a `pattern` get a PatternBadge right under them: driven by the input's own :invalid state.
 * Invalid inputs (e.g. a `pattern` mismatch) disable submit via ValidationContext,
 * unless FIELDS_PROPS sets `blockSubmitOnInvalidInputs: false` for the fieldsType.
 */
export const FieldsGroupWrapper: FC<
  React.PropsWithChildren<{
    /** Selects the static texts, button id and edit permission from FIELDS_PROPS (constants.ts). */
    fieldsType: FieldsType;
    /** Number of saved entries, shown in the NamedRow and the slide-over title. */
    initFieldsCount: number;
    /** When set, disables submit and add, and is shown instead of the FIELDS_PROPS info message. */
    disabledMessage?: string;
    /** Submit gating owned by the consumer, normally "the draft differs from the saved entries". */
    allowSubmit?: boolean;
    /** Called when the slide-over opens: copies the saved entries into the draft state. */
    initConfig: PureVoidFunc;
    onClose?: PureVoidFunc;
    /** Adds an empty draft entry (getFieldsProps' handleAdd prepends it). */
    addFieldHandler: PureVoidFunc;
    /** Removes the draft entry at the given row index. */
    removeFieldHandler: (number: number) => void;
    /** Saves the draft; resolving to true closes the slide-over. */
    submitFieldHandler: VoidBooleanPromise;
    /** One array of input props per row, usually getFieldsProps' fieldsRenderData output. */
    fieldRender: (InputFieldProps & { title?: string })[][];
    /** Keys whose values are matched by the search box, checked for duplicates / built-in values,
     * and required together: a row with some of them filled and others empty blocks submit. */
    importantFields: string[];
    /** Reserved values: an important field with one of these values gets a "built-in standard" warning. */
    builtInValues?: string[];
    /** When true, any duplicate / built-in warning also disables submit. Without it the warnings are informational only. */
    blockSubmitOnDuplicates?: boolean;
    /** False shows a skeleton instead of the rows and disables add. */
    isLoaded?: boolean;
  }>
> = ({
  fieldsType,
  initFieldsCount,
  disabledMessage,
  allowSubmit = true,
  initConfig,
  onClose,
  addFieldHandler,
  removeFieldHandler,
  submitFieldHandler,
  fieldRender,
  importantFields,
  builtInValues,
  blockSubmitOnDuplicates = false,
  isLoaded = true,
  children,
}) => {
  const [showSlideOver, setShowSlideOver] = useState(false);

  const { search, setSearch, isVisible } = useSlideOverSearch(
    showSlideOver,
    fieldRender,
    (data, search) => showItem(search, importantFields, data),
  );

  const { fieldDuplicateWarnings, completenessMessage, incompleteRows } =
    useRowChecks(fieldRender, importantFields, builtInValues);

  const {
    title,
    customId,
    editPermissions,
    wrapperTitle,
    infoMessage,
    customApproveTitle,
    searchPlaceholder,
    buttonText,
    blockSubmitOnInvalidInputs = true,
  } = FIELDS_PROPS[fieldsType];
  return (
    <>
      <NamedRow
        title={title}
        customId={customId}
        editPermissions={editPermissions}
        onClick={() => setShowSlideOver(true)}
        content={initFieldsCount}
      />
      <SlideOverWrapper
        open={showSlideOver}
        initConfig={initConfig}
        onClose={() => {
          setShowSlideOver(false);
          onClose?.();
        }}
        title={`${wrapperTitle ?? title} (${initFieldsCount})`}
        buttonProps={{
          title: customApproveTitle,
          disabled:
            !!disabledMessage ||
            !allowSubmit ||
            incompleteRows.length > 0 ||
            (blockSubmitOnDuplicates && fieldDuplicateWarnings.length > 0),
          onClick: submitFieldHandler,
        }}
        message={disabledMessage ?? infoMessage}
        blockSubmitOnInvalidInputs={blockSubmitOnInvalidInputs}
      >
        <div className="flex gap-4 max-xs:flex-col-reverse">
          <SearchBox
            placeholder={searchPlaceholder ?? "Name"}
            value={search}
            onChange={({ target }) => setSearch(target.value.toLowerCase())}
          />
          <AddComponentsButton
            valueTitle={fieldsType}
            onClick={() => {
              setSearch("");
              addFieldHandler();
            }}
            buttonText={buttonText ?? `Add ${fieldsType}`}
            canAddExtraComponent={true}
            disabled={!!disabledMessage || !isLoaded}
          />
        </div>
        <AppearedContent
          isLoaded={isLoaded}
          skeletonOptions={{
            amount: 1,
            className: "h-10 w-full my-4 rounded-md",
          }}
        >
          <div className="grid gap-4">
            {fieldRender.map((data, number) =>
              isVisible(data) ? (
                <FieldsGroup
                  key={`${fieldsType}${number}`}
                  groupId={`${fieldsType}${number}`}
                  handleRemove={() => removeFieldHandler(number)}
                >
                  {data.map((item) => (
                    // group/field: scopes PatternBadge to this input's :invalid / focus state.
                    <div key={`${item.key}${number}`} className="group/field">
                      <NamedField {...item} fieldKey={item.key} />
                      {item.pattern && (
                        <PatternBadge
                          message={patternMessage(item.key, item.pattern)}
                          type={
                            blockSubmitOnInvalidInputs ? "error" : "warning"
                          }
                        />
                      )}
                    </div>
                  ))}
                  <FieldsGroupBadges
                    duplicateMessage={
                      fieldDuplicateWarnings.find(
                        ({ index }) => index === number,
                      )?.message
                    }
                    duplicateType={
                      blockSubmitOnDuplicates ? "error" : "warning"
                    }
                    incomplete={incompleteRows.includes(number)}
                    completenessMessage={completenessMessage}
                  />
                </FieldsGroup>
              ) : null,
            )}
          </div>
          {children}
        </AppearedContent>
      </SlideOverWrapper>
    </>
  );
};
